<script setup lang="ts">
import { CertificateModel } from 'web-pki';
import { useArrayFilter, useEventListener } from '@vueuse/core';
import { XSelect, XSpinner } from '@clicksign/excalibur';
import { useI18n } from '#i18n';
import { useToast } from 'vue-toastification';
import { captureException } from '@sentry/vue';

import { useCurrentSignatureRequestKey, simpleMask, toCamelCase, useModals, isFetchError } from '~~core';
import { BrandButton } from '~~base-ui';

import DigitalCertificateMissingWebPKIModal from '../DigitalCertificateMissingWebPKIModal/DigitalCertificateMissingWebPKIModal.vue';
import DigitalCertificateWebPKIHelpModal from '../DigitalCertificateWebPKIHelpModal/DigitalCertificateWebPKIHelpModal.vue';
import DigitalCertificateErrorModal from '../DigitalCertificateErrorModal/DigitalCertificateErrorModal.vue';
import DigitalCertificateMobileWarningModal from '../DigitalCertificateMobileWarningModal/DigitalCertificateMobileWarningModal.vue';
import { startDigitalCertificateLocal, completeDigitalCertificateLocalSignature } from '../../services';
import { useWebPki } from '../../composables/use-web-pki';
import { isWebPKIError } from '../../utils/web-pki';
import { useFetchSigner } from '../../repositories/signer';

type SelectCertificateEmits = {
  (e: 'completed', certificateKey: string): void;
  (e: 'back'): void;
};

const emit = defineEmits<SelectCertificateEmits>();
const accessErrorType = ref('missingCertificates');

const modalName = {
  missingValidCertificates: 'missing-valid-certificates',
  missingCertificatesByDocumentation: 'missing-certificates-by-documentation',
  missingWebPKI: 'missing-web-pki',
  webPKIHelp: 'web-pki-help',
  accessError: 'access-error',
  mobileWarning: 'mobile-warning',
};

const MOBILE_WIDTH = 768;
const { signatureRequestKey } = useCurrentSignatureRequestKey();

const { t } = useI18n();
const toast = useToast();

const { signer } = useFetchSigner(signatureRequestKey);

const { closeModal, openModal, isModalOpen } = useModals();
const { certificates, isLoading, readCertificate, signHashBatch, redirectToInstallPage } = useWebPki({
  onNotInstalled: () => openModal(modalName.missingWebPKI),
});
const isSubmitting = ref(false);
const errorMessage = ref('');

type SelectedCertificateItem = {
  text: string,
  value: string,
};

const selectedCertificateItem = ref<SelectedCertificateItem>();
const selectedCertificate = computed(() => certificates.value.find((cert: CertificateModel) => cert.thumbprint === selectedCertificateItem.value?.value));

function isExpired(cert: CertificateModel) {
  const now = new Date();

  return cert.validityEnd < now || now < cert.validityStart;
}

function isSameCpf(cert: CertificateModel) {
  const signerCpf = signer.value?.documentation?.replace(/\D/g, '');

  return signerCpf === cert.pkiBrazil.cpf;
}

function certificateToItem(cert: CertificateModel) {
  const name = cert.pkiBrazil.responsavel ?? cert.subjectName;
  const isCompanyCertificate = !!cert.pkiBrazil.cnpj;
  const mask = isCompanyCertificate ? '##.###.###/####-##' : '###.###.###-##';
  const identifier = simpleMask(cert.pkiBrazil.cnpj ?? cert.pkiBrazil.cpf ?? '', mask);

  const text = isCompanyCertificate
    ? `${name} ${t('requirement.common.company')} ${cert.pkiBrazil.companyName} ${identifier}`
    : `${name} ${identifier}`;

  return {
    text: text.toUpperCase().trim(),
    value: cert.thumbprint,
  };
}

function createGroup(header: string, groupItems: CertificateModel[], options: { disabled?: boolean } = {}) {
  if (groupItems.length === 0) return [];

  return [
    { header },
    ...groupItems.map(certificateToItem).map((item) => ({ ...item, disabled: options.disabled })),
  ];
}

function handleWindowResize() {
  if (window.parent.innerWidth < MOBILE_WIDTH) openModal(modalName.mobileWarning);
}

useEventListener('resize', handleWindowResize, { passive: true });
handleWindowResize();

const expiredCertificates = useArrayFilter(certificates, isExpired);
const missMatchCpfCertificates = useArrayFilter(certificates, (cert) => !isExpired(cert) && !isSameCpf(cert));
const validCertificates = useArrayFilter(certificates, (cert) => !isExpired(cert) && isSameCpf(cert));

const items = computed(() => [
  ...createGroup(t('requirement.common.valid', validCertificates.value), validCertificates.value),
  ...createGroup(t('requirement.common.expired', expiredCertificates.value.length), expiredCertificates.value, { disabled: true }),
  ...createGroup(t('requirement.digitalCertificate.local.noRelatedCpf'), missMatchCpfCertificates.value, { disabled: true }),
]);

function openAccessErrorModal(error: string) {
  accessErrorType.value = error;
  openModal(modalName.accessError);
}

function backToChooseSubstep() {
  closeModal();
  emit('back');
}

async function start() {
  if (!selectedCertificate.value) return;

  const { thumbprint } = selectedCertificate.value;
  const certContent = await readCertificate(thumbprint);
  const startDocumentsData = await startDigitalCertificateLocal(
    signatureRequestKey.value,
    { certContent },
  );

  const batches = startDocumentsData.map((data) => data.toSignHash);

  if (!startDocumentsData.length) throw new Error('Documents not found');

  const { signatures } = await signHashBatch(thumbprint, batches, startDocumentsData[0].digestAlgorithmName);

  if (!signatures) throw new Error('Signatures not found');

  return signatures.map((signature, index) => ({
    documentKey: startDocumentsData?.[index]?.documentKey,
    transferFileId: startDocumentsData?.[index]?.transferFileId,
    signature,
    version: startDocumentsData?.[index]?.version,
  }));
}

const localePath = useLocalePath();
const route = useRoute();

async function submit() {
  try {
    errorMessage.value = '';
    isSubmitting.value = true;
    const icpSignatures = await start();

    if (!icpSignatures) throw new Error('icpSignatures is undefined');

    const { certificateKey } = await completeDigitalCertificateLocalSignature(signatureRequestKey.value, {
      icpSignatures,
    });

    emit('completed', certificateKey);
  } catch (error) {
    if (isFetchError(error) && error?.data?.error?.includes('ConversionPdfa')) {
      navigateTo(localePath({
        name: 'signatureRequestKind-signatureRequestKey-feedback',
        params: route.params,
        query: { ...route.query, status: 'digital_certificate_pdfa_error' },
      }));

      return captureException(new Error('digital_certificate_pdfa_error'));
    }

    if (!isWebPKIError(error)) {
      toast.error(t('requirement.digitalCertificate.local.error.unexpected'));
      throw error;
    }

    const errorCode = toCamelCase(error?.code) || 'default';

    errorMessage.value = t(`requirement.digitalCertificate.local.error.${errorCode}`);
  } finally {
    isSubmitting.value = false;
  }
}

function installWebPki() {
  redirectToInstallPage();
}

watch(
  certificates,
  () => {
    if (certificates.value.length === 0) openAccessErrorModal('missingCertificates');
    else if (expiredCertificates.value.length === certificates.value.length) openAccessErrorModal('missingValidCertificates');
    else if (validCertificates.value.length === 0) openAccessErrorModal('missingCertificatesByDocumentation');
  },
);
</script>

<template>
  <section :class="$style.digitalCertificateLocal">
    <div
      v-if="isLoading && items"
      :class="$style.loading"
      data-testid="digitalCertificateLoading"
    >
      <XSpinner size="large" />

      <h2>
        {{ $t('requirement.common.loading') }}
      </h2>
    </div>

    <div v-else>
      <h4>
        {{ $t('requirement.digitalCertificate.local.selectCertificate') }}
      </h4>

      <div :class="$style.certificates">
        <XSelect
          v-model="selectedCertificateItem"
          :items="items"
          :max-items="4"
          :disabled="isSubmitting"
          :custom-error="errorMessage"
          return-object
          full-width
          data-testid="selectCertificate"
        />

        <BrandButton
          :disabled="!selectedCertificateItem"
          :loading="isSubmitting && !errorMessage"
          data-testid="digitalCertificateLocalSignButton"
          @click="submit"
        >
          {{ $t('requirement.common.sign') }}
        </BrandButton>
      </div>
    </div>

    <DigitalCertificateErrorModal
      v-if="isModalOpen(modalName.accessError)"
      :error="accessErrorType"
      @close="backToChooseSubstep()"
    />

    <DigitalCertificateMobileWarningModal
      v-if="isModalOpen(modalName.mobileWarning)"
      @close="backToChooseSubstep()"
    />

    <DigitalCertificateMissingWebPKIModal
      v-if="isModalOpen(modalName.missingWebPKI)"
      @what-is-web-pki="openModal(modalName.webPKIHelp)"
      @install-web-pki="installWebPki()"
      @close="backToChooseSubstep()"
    />

    <DigitalCertificateWebPKIHelpModal
      v-if="isModalOpen(modalName.webPKIHelp)"
      @close="openModal(modalName.missingWebPKI)"
    />
  </section>
</template>

<style module lang="scss">
.digitalCertificateLocal {
  width: 100%;
}

.certificates {
  display: flex;
  flex-direction: column;
  min-height: 200px;
  margin-bottom: var(--space-regular);
  gap: var(--space-large-x);
}

.loading {
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: var(--space-large-xxx);
  gap: var(--space-regular);
}
</style>
